import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import {
  catchError,
  filter,
  from,
  map,
  mergeMap,
  Observable,
  of,
  switchMap,
  takeUntil,
  timer,
  withLatestFrom,
} from 'rxjs';

import { NotificationService } from '../../services/notification.service';
import { NotificationActions } from '../actions/notification.actions';
import { selectUnreadNotificationIds } from '../selectors/notification.selector';

const POLL_INTERVAL_MS = 20000;

@Injectable()
export class NotificationEffects {
  public startPolling$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(NotificationActions.startPolling),
      switchMap(() =>
        timer(0, POLL_INTERVAL_MS).pipe(
          takeUntil(
            this.actions$.pipe(ofType(NotificationActions.stopPolling)),
          ),
          map(() => NotificationActions.load()),
        ),
      ),
    ),
  );

  public load$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(NotificationActions.load),
      switchMap(() =>
        from(this.service.list()).pipe(
          map((data) => NotificationActions.loadSuccess({ data })),
          catchError((error) => of(NotificationActions.loadFailure({ error }))),
        ),
      ),
    ),
  );

  public markRead$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(NotificationActions.markRead),
      mergeMap(({ id }) =>
        from(this.service.markRead(id)).pipe(
          map(() => NotificationActions.markReadSuccess({ id })),
          catchError((error) =>
            of(NotificationActions.markReadFailure({ error })),
          ),
        ),
      ),
    ),
  );

  public markAllRead$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(NotificationActions.markAllRead),
      withLatestFrom(this.store.select(selectUnreadNotificationIds)),
      filter(([, ids]) => ids.length > 0),
      mergeMap(([, ids]) =>
        from(this.service.markAllRead(ids)).pipe(
          map((data) => NotificationActions.markAllReadSuccess({ data })),
          catchError((error) =>
            of(NotificationActions.markAllReadFailure({ error })),
          ),
        ),
      ),
    ),
  );

  public constructor(
    private readonly actions$: Actions,
    private readonly service: NotificationService,
    private readonly store: Store,
  ) {}
}
